import React from 'react';
import styled from 'styled-components/macro';
import { useTranslate } from '@federico.moretti/react-translate';
import Text from '../Text';
import { toFixedString } from '../../utils/numbers';
import { SIZE, NUMBER_VALUE_TYPE } from '../../types';

const NumberValueWrapper = styled.output`
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: baseline;
`;

const sizes: { [key: string]: SIZE[] } = {
  xxxs: ['xxxs', 'xxxs'],
  xxs: ['xxs', 'xxs'],
  xs: ['xs', 'xs'],
  sm: ['sm', 'sm'],
  base: ['base', 'sm'],
  lg: ['lg', 'sm'],
  xl: ['xl', 'sm'],
  xxl: ['xxl', 'lg'],
  xxxl: ['xxxl', 'xl'],
  xxxxl: ['xxxxl', 'xxl'],
};

type SIGN = 'auto' | 'always' | 'positive' | 'negative' | 'none';
function getSign(value: number, sign: SIGN) {
  switch (sign) {
    case 'auto':
      return value >= 0 ? '' : '-';
    case 'always':
      return value >= 0 ? '+' : '-';
    case 'positive':
      return '+';
    case 'negative':
      return '-';
    default:
      return '';
  }
}

type NumberValueBaseProps = {
  value: number;
  id?: string;
  testid?: string;
  size?: SIZE;
  sign?: SIGN;
  lang?: string;
  style?: React.CSSProperties;
  digits?: number;
};

type NumberValueProps =
  | (NumberValueBaseProps & { type: 'currency'; currency: string })
  | (NumberValueBaseProps & { type?: Omit<'currency', NUMBER_VALUE_TYPE> });

function NumberValue(props: NumberValueProps) {
  const {
    id,
    value,
    size = 'base',
    sign = 'auto',
    style = {},
    lang,
    testid = 'number-label',
    digits = 2,
  } = props;

  const type = 'type' in props ? props.type : undefined;
  const currency = 'currency' in props ? props.currency : undefined;

  const { t } = useTranslate();

  if (value == null) {
    return (
      <Text id={id} testid={testid} margin="none" size={size} style={style}>
        nd
      </Text>
    );
  }

  if (type === 'percentage') {
    const percent = toFixedString(Math.abs(value) * 100, lang, digits) + '%';
    const int = percent.slice(0, -(digits + 1));
    const decimal = percent.slice(-(digits + 1));

    return (
      <NumberValueWrapper id={id} aria-label={percent} style={style}>
        <Text
          testid={testid}
          margin="none"
          size={sizes[size][0]}
          style={{ color: style.color }}
        >
          {`${getSign(value, sign)}${int}`}
        </Text>
        <Text
          testid={`${testid}-decimal`}
          margin="none"
          size={sizes[size][1]}
          style={{ color: style.color }}
        >
          {decimal}
        </Text>
      </NumberValueWrapper>
    );
  }

  if (type === 'day' || type === 'year') {
    const unit = type === 'year' ? 'year' : 'day';
    const label = t(`${unit}sCount`, { count: value });

    return (
      <NumberValueWrapper id={id} aria-label={label} style={style}>
        <Text
          testid={testid}
          margin="none"
          size={sizes[size][0]}
          style={{ color: style.color }}
        >
          {`${getSign(value, sign)}${Math.abs(value)}`}
        </Text>
        <Text
          margin="none"
          transform="lowercase"
          size={sizes[size][1]}
          style={{
            marginLeft: 3,
            color: style.color,
          }}
        >
          {' '}
          {t(unit, { count: value })}
        </Text>
      </NumberValueWrapper>
    );
  }

  const fixedValue = toFixedString(Math.abs(value), lang, digits);

  if (type === 'currency' && currency) {
    const int = fixedValue.slice(0, -(digits + 1));
    const decimal = fixedValue.slice(-(digits + 1));

    return (
      <NumberValueWrapper
        id={id}
        aria-label={fixedValue + currency}
        style={style}
      >
        <Text
          testid={testid}
          margin="none"
          size={sizes[size][0]}
          style={{ color: style.color }}
        >
          {`${getSign(value, sign)}${int}`}
        </Text>
        <Text
          testid={`${testid}-decimal`}
          margin="none"
          size={sizes[size][1]}
          style={{ color: style.color }}
        >
          {`${decimal}${currency}`}
        </Text>
      </NumberValueWrapper>
    );
  }

  if (Number.isInteger(value)) {
    return (
      <Text
        id={id}
        testid={testid}
        margin="none"
        size={sizes[size][0]}
        style={style}
      >
        {`${getSign(value, sign)}${Math.abs(value)}`}
      </Text>
    );
  }

  const int = fixedValue.slice(0, -(digits + 1));
  const decimal = fixedValue.slice(-(digits + 1));
  return (
    <NumberValueWrapper id={id} aria-label={fixedValue} style={style}>
      <Text testid={testid} margin="none" size={sizes[size][0]} style={style}>
        {`${getSign(value, sign)}${int}`}
      </Text>
      <Text
        testid={`${testid}-decimal`}
        margin="none"
        size={sizes[size][1]}
        style={style}
      >
        {decimal}
      </Text>
    </NumberValueWrapper>
  );
}

export default React.memo(NumberValue);
